import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, ShoppingCart, BookOpen, ArrowLeft, ListChecks, VideoCamera, Clock, Sparkle, CalendarBlank, Info, CheckCircle } from "@phosphor-icons/react";

const typeLabel = { phonics: "Phonics", sightwords: "Sight Words", story: "Story", comprehension: "Comprehension", vocabulary: "Vocabulary", explore: "Explore & Learn" };
const SLOTS = ["9:00 AM", "10:00 AM", "11:00 AM", "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM"];

export default function PackageDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem, inCart } = useCart();
  const { user } = useAuth();
  const [pkg, setPkg] = useState(null);
  const [ownedItem, setOwnedItem] = useState(null);
  const [date, setDate] = useState();
  const [time, setTime] = useState("");
  const [booking, setBooking] = useState(false);

  useEffect(() => {
    api.get(`/packages/${slug}`).then((r) => setPkg(r.data)).catch(() => navigate("/catalog"));
  }, [slug, navigate]);

  useEffect(() => {
    if (user && pkg) {
      api.get("/library").then((r) => setOwnedItem(r.data.find((p) => p.id === pkg.id) || null)).catch(() => {});
    }
  }, [user, pkg]);

  if (!pkg) return <div className="min-h-[60vh] grid place-items-center font-body text-ink-soft">Loading…</div>;

  const cart = inCart(pkg.id);
  const owned = !!ownedItem;
  const isTutor = pkg.kind === "tutoring";

  const requestConsultation = async () => {
    if (!user) { toast.info("Please sign in to request a consultation."); navigate("/login"); return; }
    if (!date || !time) { toast.error("Please choose a preferred date and time."); return; }
    setBooking(true);
    try {
      const iso = date.toISOString().split("T")[0];
      const label = `${date.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })} at ${time}`;
      const res = await api.post("/payments/checkout", {
        lookup_keys: [pkg.lookup_key],
        origin_url: window.location.origin,
        booking: { date: iso, time, label },
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error("Could not start your request. Please try again.");
      setBooking(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="package-detail-page">
      <Link to="/catalog" className="inline-flex items-center gap-2 font-body font-semibold text-ink-soft hover:text-brand-sky mb-6">
        <ArrowLeft size={18} weight="bold" /> Back to catalog
      </Link>

      <div className="grid lg:grid-cols-2 gap-10">
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="relative">
          <img src={pkg.image} alt={pkg.title} className="rounded-[2rem] sticker w-full h-[380px] object-cover" />
          <span className="absolute top-4 left-4 px-4 py-1.5 rounded-full text-sm font-bold text-white tracking-[0.15em] uppercase" style={{ backgroundColor: pkg.color }}>
            {pkg.level}
          </span>
        </motion.div>

        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl text-ink tracking-tight">{pkg.title}</h1>
          <p className="font-body text-lg text-ink-soft mt-3 leading-relaxed">{pkg.description}</p>

          <div className="flex items-center gap-5 mt-6 font-body font-semibold text-ink-soft">
            {isTutor ? (
              <>
                <span className="flex items-center gap-2"><Clock size={20} weight="duotone" style={{ color: pkg.color }} /> {pkg.duration}</span>
                <span className="flex items-center gap-2"><VideoCamera size={20} weight="duotone" style={{ color: pkg.color }} /> {pkg.format}</span>
              </>
            ) : (
              <>
                <span className="flex items-center gap-2"><BookOpen size={20} weight="duotone" style={{ color: pkg.color }} /> {pkg.lesson_count} lessons</span>
                <span className="flex items-center gap-2"><ListChecks size={20} weight="duotone" style={{ color: pkg.color }} /> Lifetime access</span>
              </>
            )}
          </div>

          {/* Non-tutor purchase actions */}
          {!isTutor && (
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <span className="font-heading font-bold text-4xl text-ink">${Number(pkg.price).toFixed(0)}</span>
              {owned ? (
                <Button onClick={() => navigate(`/read/${pkg.slug}`)} className="rounded-full bg-brand-grass hover:bg-brand-grass/90 text-white font-bold text-lg px-8 py-6" data-testid="start-reading-btn">
                  Start reading →
                </Button>
              ) : cart ? (
                <Button onClick={() => navigate("/cart")} className="rounded-full bg-brand-sun hover:bg-brand-sunlight text-ink font-bold text-lg px-6 sm:px-8 py-6" data-testid="go-to-cart-btn">
                  <Check size={20} weight="bold" className="mr-2" /> In cart — checkout
                </Button>
              ) : (
                <Button onClick={() => { addItem(pkg); toast.success("Added to cart!"); }} className="rounded-full bg-brand-sky hover:bg-brand-skylight text-white font-bold text-lg px-6 sm:px-8 py-6 transition-transform hover:-translate-y-1" data-testid="detail-add-to-cart-btn">
                  <ShoppingCart size={20} weight="duotone" className="mr-2" /> Add to cart
                </Button>
              )}
            </div>
          )}

          {/* Tutor: price + consultation emphasis */}
          {isTutor && (
            <div className="mt-8">
              <span className="font-heading font-bold text-4xl text-ink">
                <span className="block font-body text-sm font-semibold text-ink-soft tracking-wide">Starting at</span>
                ${Number(pkg.price).toFixed(0)}<span className="text-xl font-semibold text-ink-soft">/hr</span>
              </span>
              <div className="mt-5 flex items-start gap-3 rounded-2xl p-4 border-2" style={{ borderColor: `${pkg.color}40`, backgroundColor: `${pkg.color}0d` }} data-testid="consultation-notice">
                <Info size={22} weight="duotone" className="shrink-0 mt-0.5" style={{ color: pkg.color }} />
                <p className="font-body text-sm text-ink leading-relaxed">
                  <span className="font-bold">A consultation comes first.</span> Every session begins with a friendly consultation to make sure it's the right fit — <span className="font-semibold">nothing is final until we talk.</span> Pick a preferred time below and we'll reach out to confirm the details. <span className="italic">{pkg.disclaimer}</span>
                </p>
              </div>
            </div>
          )}
        </motion.div>
      </div>

      {/* Tutor booking / benefits */}
      {isTutor && (
        <div className="mt-12 grid lg:grid-cols-2 gap-8">
          <div className="bg-white sticker rounded-3xl p-7">
            <h3 className="font-heading font-bold text-xl text-ink mb-4">How your session works</h3>
            <div className="space-y-3">
              {(pkg.benefits || []).map((b, i) => (
                <div key={i} className="flex items-start gap-4" data-testid={`benefit-${i}`}>
                  <span className="grid place-items-center w-9 h-9 rounded-xl shrink-0 text-white" style={{ backgroundColor: pkg.color }}>
                    <Sparkle size={18} weight="fill" />
                  </span>
                  <p className="font-body font-medium text-ink leading-relaxed">{b}</p>
                </div>
              ))}
            </div>
            <div className="mt-5 rounded-2xl p-4" style={{ backgroundColor: `${pkg.color}12` }}>
              <p className="font-body text-sm text-ink-soft leading-relaxed">
                Best for <span className="font-semibold text-ink">ready learners who need direction</span> finding the right level. <span className="italic">{pkg.disclaimer}</span>
              </p>
            </div>
          </div>

          {owned ? (
            <div className="bg-white sticker rounded-3xl p-7 flex flex-col justify-center text-center" data-testid="tutor-owned">
              <CheckCircle size={52} weight="fill" className="mx-auto text-brand-grass" />
              <h3 className="font-heading font-bold text-2xl text-ink mt-4">Consultation requested!</h3>
              {ownedItem?.booking?.label && (
                <p className="font-body text-ink mt-2">Preferred time: <span className="font-semibold">{ownedItem.booking.label}</span></p>
              )}
              <p className="font-body text-ink-soft mt-2 leading-relaxed">
                Our team will email you shortly to confirm your session and finalize the details together.
              </p>
              <Link to="/library"><Button className="mt-5 rounded-full bg-brand-grass hover:bg-brand-grass/90 text-white font-bold px-8 py-6">Go to my bookshelf</Button></Link>
            </div>
          ) : (
            <div className="bg-white sticker rounded-3xl p-7" data-testid="booking-panel">
              <div className="flex items-center gap-2 mb-4">
                <CalendarBlank size={22} weight="duotone" style={{ color: pkg.color }} />
                <h3 className="font-heading font-bold text-xl text-ink">Choose a preferred time</h3>
              </div>
              <div className="grid sm:grid-cols-[auto_1fr] gap-6">
                <div className="rounded-2xl border-2 border-slate-900/5 p-2 w-fit">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={{ before: new Date() }}
                    data-testid="booking-calendar"
                  />
                </div>
                <div>
                  <p className="font-body font-semibold text-sm text-ink-soft mb-2">Available time slots</p>
                  <div className="grid grid-cols-2 gap-2">
                    {SLOTS.map((s) => (
                      <button
                        key={s}
                        onClick={() => setTime(s)}
                        className={`px-3 py-2.5 rounded-xl font-body font-semibold text-sm border-2 transition-colors ${
                          time === s ? "text-white" : "text-ink border-slate-900/10 hover:border-brand-sky"
                        }`}
                        style={time === s ? { backgroundColor: pkg.color, borderColor: pkg.color } : {}}
                        data-testid={`slot-${s.replace(/[:\s]/g, "")}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                  {date && time && (
                    <p className="font-body text-sm text-ink mt-4" data-testid="selected-slot">
                      Selected: <span className="font-semibold">{date.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })} at {time}</span>
                    </p>
                  )}
                </div>
              </div>
              <Button
                onClick={requestConsultation}
                disabled={booking}
                className="w-full mt-6 rounded-full text-white font-bold text-lg py-6 transition-transform hover:-translate-y-0.5"
                style={{ backgroundColor: pkg.color }}
                data-testid="request-consultation-btn"
              >
                <VideoCamera size={20} weight="duotone" className="mr-2" />
                {booking ? "Redirecting…" : "Request consultation"}
              </Button>
              <p className="font-body text-xs text-ink-soft text-center mt-3">
                This reserves your preferred time as a request. We'll confirm your session by email before anything is final. <span className="italic">{pkg.disclaimer}</span>
              </p>
            </div>
          )}
        </div>
      )}

      {/* Non-tutor: what's inside */}
      {!isTutor && (
        <div className="mt-10 max-w-3xl">
          <h3 className="font-heading font-bold text-xl text-ink mb-4">What's inside</h3>
          <div className="space-y-3">
            {(pkg.lessons || []).map((l, i) => (
              <div key={l.id} className="flex items-center gap-4 bg-white sticker rounded-2xl p-4" data-testid={`lesson-preview-${i}`}>
                <span className="grid place-items-center w-10 h-10 rounded-xl font-heading font-bold text-white" style={{ backgroundColor: pkg.color }}>{i + 1}</span>
                <div className="flex-1">
                  <p className="font-heading font-semibold text-ink">{l.title}</p>
                  <p className="font-body text-xs uppercase tracking-wider text-ink-soft">{typeLabel[l.type] || l.type}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
